import { FC, memo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { T_KEYS } from '../../constants';

interface IProgressModalProps {
  isOpen: boolean;
  title: string;
  description?: string;
  onPrimaryAction: () => void;
  onSecondaryAction?: () => void;
  primaryButtonText?: string;
  secondaryButtonText?: string;
  showNotification?: boolean;
  notificationText?: string;
  isRtl?: boolean;
}

const Overlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: ${(props) => (props.$isOpen ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: fadeIn 0.2s ease;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const ModalContainer = styled.div<{ $isRtl?: boolean }>`
  background: white;
  border-radius: 24px;
  padding: 40px;
  max-width: 480px;
  width: 90%;
  box-shadow: 0px 20px 40px rgba(16, 24, 40, 0.15);
  animation: slideUp 0.3s ease;
  text-align: center;
  direction: ${(props) => (props.$isRtl ? 'rtl' : 'ltr')};

  @keyframes slideUp {
    from {
      transform: translateY(20px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
`;

const IconContainer = styled.div`
  width: 80px;
  height: 80px;
  background: #F8F7FF;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;
`;

const ClockIcon = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid #5B3FFF;
  border-radius: 50%;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 2px;
    height: 12px;
    background: #5B3FFF;
    transform-origin: bottom center;
    transform: translate(-50%, -100%) rotate(0deg);
    animation: clockHand 2s linear infinite;
  }

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 2px;
    height: 8px;
    background: #5B3FFF;
    transform-origin: bottom center;
    transform: translate(-50%, -100%) rotate(90deg);
  }

  @keyframes clockHand {
    from {
      transform: translate(-50%, -100%) rotate(0deg);
    }
    to {
      transform: translate(-50%, -100%) rotate(360deg);
    }
  }
`;

const Title = styled.h2`
  font-weight: 700;
  font-size: 20px;
  color: #5B3FFF;
  margin: 0 0 12px;
`;

const Description = styled.p`
  font-size: 14px;
  color: #697586;
  line-height: 1.6;
  margin: 0 0 32px;
`;

const ButtonsColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const PrimaryButton = styled.button`
  width: 100%;
  padding: 14px 24px;
  background: #5B3FFF;
  color: white;
  border: none;
  border-radius: 12px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: #4A2FE6;
  }
`;

const SecondaryButton = styled.button`
  width: 100%;
  padding: 14px 24px;
  background: white;
  color: #344054;
  border: 1px solid #D0D5DD;
  border-radius: 12px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: #F9FAFB;
  }
`;

const NotificationText = styled.p`
  font-size: 12px;
  color: #9CA3AF;
  margin: 16px 0 0;
`;

const ProgressModal: FC<IProgressModalProps> = memo(({
  isOpen,
  title,
  description,
  onPrimaryAction,
  onSecondaryAction,
  primaryButtonText,
  secondaryButtonText,
  showNotification = true,
  notificationText,
  isRtl = false,
}) => {
  const { t } = useTranslation();

  return (
    <Overlay $isOpen={isOpen}>
      <ModalContainer $isRtl={isRtl}>
        <IconContainer>
          <ClockIcon />
        </IconContainer>
        <Title>{title}</Title>
        {description && <Description>{description}</Description>}
        <ButtonsColumn>
          <PrimaryButton onClick={onPrimaryAction}>
            {primaryButtonText || t(T_KEYS.GO_TO_DASHBOARD)}
          </PrimaryButton>
          {onSecondaryAction && (
            <SecondaryButton onClick={onSecondaryAction}>
              {secondaryButtonText || t(T_KEYS.STAY_HERE)}
            </SecondaryButton>
          )}
        </ButtonsColumn>
        {showNotification && (
          <NotificationText>
            {notificationText || t(T_KEYS.NOTIFICATION_ON_COMPLETE)}
          </NotificationText>
        )}
      </ModalContainer>
    </Overlay>
  );
});

ProgressModal.displayName = 'ProgressModal';

export default ProgressModal;
